import {useCallback, useMemo, useState} from 'react';

import {UserAmsRole} from 'modules/decisionsNext/api/types';
import {BasicTreeViewSelectionOptions, TreeViewSelection} from 'filters/features/FilterDialog/types';

import {
  getIdsWithSourceCodes,
  transformUserTreeIntoMap,
} from 'modules/decisionsNext/hooks/trees/userTrees/useProducerTree';
import {getInternalId, getOriginalId} from 'modules/decisionsNext/hooks/trees/userTrees/useUserTree';
import {GenericTreeNode, groupSelectedKeys} from 'utils/treeUtils';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {FeatureFlags} from 'core/constants/featureFlags';
import {openFilterDialog} from 'filters';

import {TreeBasicNode} from 'components/Tree/TreeTypes';

import {SelectedTooltip} from 'filters/features/FilterDialog/components/SelectedTooltip';
import {CounterTooltip} from 'filters/components/CounterTooltip';

type UserSourceCode = {
  userId: string;
  sourceCodes?: string[] | null;
};

export type TeamsTreeValue = {
  accountManagers?: UserSourceCode[];
  accountExecutives?: UserSourceCode[];
  producers?: UserSourceCode[];
};

const splitTeamsKeys = (keys: Set<string>) => {
  const accountManagers = new Set<string>();
  const accountExecutives = new Set<string>();
  const producers = new Set<string>();

  keys.forEach(key => {
    key.endsWith(UserAmsRole.AccountExecutive) && accountExecutives.add(key);
    key.endsWith(UserAmsRole.AccountManager) && accountManagers.add(key);
    key.endsWith(UserAmsRole.Producer) && producers.add(key);

    if (key === 'all-account-executives') {
      accountExecutives.add(key);
    }

    if (key === 'all-account-managers') {
      accountManagers.add(key);
    }

    if (key === 'all-producers') {
      producers.add(key);
    }
  });

  return {accountManagers, accountExecutives, producers};
};

const splitUsersAndCodes = (keys: Set<string>) => {
  const users = new Set<string>();
  const codes = new Set<string>();

  keys.forEach(key => {
    const [_, code] = getOriginalId(key);

    code ? codes.add(key) : users.add(key);
  });

  return {users, codes};
};

const getInternalIds = (users: UserSourceCode[] | undefined, role: UserAmsRole) => {
  return (users || [])?.reduce((acc: string[], id) => {
    const sameUserIds = id.sourceCodes?.map(item => getInternalId({userId: id.userId, amsCodeId: item, role})) || [];

    if (sameUserIds.length) {
      acc.push(...sameUserIds);
    } else {
      acc.push(getInternalId({userId: id.userId, role}));
    }

    return acc;
  }, []);
};

export type TeamsTreeViewSelectionOptions = BasicTreeViewSelectionOptions & {
  disableAccountExecutives?: boolean;
  disableAccountManagers?: boolean;
  disableProducers?: boolean;
};

export const useTeamsTreeViewSelection: TreeViewSelection<
  string,
  GenericTreeNode<TreeBasicNode>,
  TeamsTreeValue,
  TeamsTreeViewSelectionOptions
> = (
  {treeData, treeMap},
  {value, setValue},
  {disabled, disableProducers, disableAccountManagers, disableAccountExecutives}
) => {
  const withAccountExecutives = useFeatureFlag(FeatureFlags.AccountExecutives);

  const [isAllAccountExecutivesSelected, setIsAllAccountExecutivesSelected] = useState(false);
  const [isAllAccountManagersSelected, setIsAllAccountManagersSelected] = useState(false);
  const [isAllProducersSelected, setIsAllProducersSelected] = useState(false);

  const {accountExecutivesTreeMap, accountManagersTreeMap, producerTreeMap} = useMemo(
    () => ({
      accountExecutivesTreeMap: transformUserTreeIntoMap(
        treeData.find(item => item.key === 'all-account-executives')?.children ?? []
      ),
      accountManagersTreeMap: transformUserTreeIntoMap(
        treeData.find(item => item.key === 'all-account-managers')?.children ?? []
      ),
      producerTreeMap: transformUserTreeIntoMap(treeData.find(item => item.key === 'all-producers')?.children ?? []),
    }),
    [treeData]
  );

  const selected = useMemo(
    () =>
      [
        getInternalIds(value.accountExecutives, UserAmsRole.AccountExecutive),
        getInternalIds(value.accountManagers, UserAmsRole.AccountManager),
        getInternalIds(value.producers, UserAmsRole.Producer),
      ].flat(),
    [value.accountExecutives, value.accountManagers, value.producers]
  );

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const groupedKeys = groupSelectedKeys(treeData, newSelected);
      const {accountExecutives, accountManagers, producers} = splitTeamsKeys(groupedKeys);

      setIsAllAccountExecutivesSelected(accountExecutives.has('all-account-executives'));
      accountExecutives.delete('all-account-executives');

      setIsAllAccountManagersSelected(accountManagers.has('all-account-managers'));
      accountManagers.delete('all-account-managers');

      setIsAllProducersSelected(producers.has('all-producers'));
      producers.delete('all-producers');

      const accountExecutivesGrouped = getIdsWithSourceCodes(Array.from(accountExecutives), accountExecutivesTreeMap);
      const producersGrouped = getIdsWithSourceCodes(Array.from(producers), producerTreeMap);
      const accountManagersGrouped = getIdsWithSourceCodes(Array.from(accountManagers), accountManagersTreeMap);

      setValue({
        accountExecutives: accountExecutivesGrouped.length ? accountExecutivesGrouped : undefined,
        producers: producersGrouped.length ? producersGrouped : undefined,
        accountManagers: accountManagersGrouped.length ? accountManagersGrouped : undefined,
      });
    },
    [accountExecutivesTreeMap, accountManagersTreeMap, producerTreeMap, setValue, treeData]
  );

  return {
    disabledKeys: useMemo(() => {
      const disabledKeys: string[] = [];

      (disableAccountExecutives || disabled) && disabledKeys.push('all-account-executives');
      (disableAccountManagers || disabled) && disabledKeys.push('all-account-managers');
      (disableProducers || disabled) && disabledKeys.push('all-producers');

      return disabledKeys;
    }, [disableAccountExecutives, disableAccountManagers, disableProducers, disabled]),
    selected: useMemo(
      () =>
        [
          isAllAccountExecutivesSelected && 'all-account-executives',
          isAllAccountManagersSelected && 'all-account-managers',
          isAllProducersSelected && 'all-producers',
          ...selected,
        ].filter(Boolean),
      [isAllAccountExecutivesSelected, isAllAccountManagersSelected, isAllProducersSelected, selected]
    ),
    setSelected,
    selectionCount: useMemo(() => {
      if (selected.length === 0) {
        return;
      }

      const {accountExecutives, accountManagers, producers} = splitTeamsKeys(new Set(selected));
      const producerGroups = splitUsersAndCodes(producers);
      const managerGroups = splitUsersAndCodes(accountManagers);
      const executiveGroups = splitUsersAndCodes(accountExecutives);

      return (
        <>
          {!disableProducers && (
            <SelectedTooltip
              title={
                producers.size ? (
                  <CounterTooltip
                    total={`Producers selected: ${producers.size}`}
                    groups={[
                      {
                        title: 'Producer',
                        count: producerGroups.users.size,
                      },
                      {
                        title: 'Producer AMS Code',
                        count: producerGroups.codes.size,
                      },
                    ]}
                  />
                ) : null
              }
            >
              Producers: {producers.size || 'All'}
            </SelectedTooltip>
          )}
          {!disableAccountManagers && (
            <SelectedTooltip
              title={
                accountManagers.size ? (
                  <CounterTooltip
                    total={`Account Managers selected: ${accountManagers.size}`}
                    groups={[
                      {
                        title: 'Account Manager',
                        count: managerGroups.users.size,
                      },
                      {
                        title: 'Account Manager AMS Code',
                        count: managerGroups.codes.size,
                      },
                    ]}
                  />
                ) : null
              }
            >
              Managers: {accountManagers.size || 'All'}
            </SelectedTooltip>
          )}
          {withAccountExecutives && !disableAccountExecutives && (
            <SelectedTooltip
              title={
                accountExecutives.size ? (
                  <CounterTooltip
                    total={`Account Executives selected: ${accountExecutives.size}`}
                    groups={[
                      {
                        title: 'Account Executive',
                        count: executiveGroups.users.size,
                      },
                      {
                        title: 'Account Executive AMS Code',
                        count: executiveGroups.codes.size,
                      },
                    ]}
                  />
                ) : null
              }
            >
              Executives: {accountExecutives.size || 'All'}
            </SelectedTooltip>
          )}
        </>
      );
    }, [disableAccountExecutives, disableAccountManagers, disableProducers, selected, withAccountExecutives]),
    overrideActiveFilterContent: ({sectionId}) => {
      const renderPair = ({
        codes = [],
        role,
        disableEntity,
        entity,
        entitySingular,
      }: {
        codes?: UserSourceCode[];
        role: UserAmsRole;
        disableEntity?: boolean;
        entity: string;
        entitySingular: string;
      }) => {
        const users = codes.filter(item => !item.sourceCodes?.length);
        const sourceCodes = codes.filter(item => item.sourceCodes?.length);

        return [
          users.length && {
            id: entity,
            label: entity,
            disabled: disableEntity || disabled,
            chips: users.map(user => {
              const id = getInternalId({userId: user.userId, role});

              return {
                id,
                label: treeMap.get(id)?.title,
                disabled: disableEntity || disabled,
                onClick: () => openFilterDialog(sectionId),
                onDelete: () => setSelected(selected.filter(selected => selected !== id)),
              };
            }),
          },
          sourceCodes.length && {
            id: `${entitySingular}-ams-codes`,
            label: `${entitySingular} AMS Codes`,
            disabled: disableEntity || disabled,
            chips: sourceCodes.flatMap(user =>
              user.sourceCodes?.map(code => {
                const id = getInternalId({userId: user.userId, amsCodeId: code, role});

                return {
                  id,
                  label: `${treeMap.get(getInternalId({userId: user.userId, role}))?.title} - ${code}`,
                  disabled: disableEntity || disabled,
                  onClick: () => openFilterDialog(sectionId),
                  onDelete: () => setSelected(selected.filter(selected => selected !== id)),
                };
              })
            ),
          },
        ];
      };

      return [
        ...renderPair({
          codes: value.producers,
          role: UserAmsRole.Producer,
          disableEntity: disableProducers,
          entity: 'Producers',
          entitySingular: 'Producer',
        }),
        ...renderPair({
          codes: value.accountManagers,
          role: UserAmsRole.AccountManager,
          disableEntity: disableAccountManagers,
          entity: 'Account Managers',
          entitySingular: 'Account Manager',
        }),
        ...(withAccountExecutives
          ? renderPair({
              codes: value.accountExecutives,
              role: UserAmsRole.AccountExecutive,
              disableEntity: disableAccountExecutives,
              entity: 'Account Executives',
              entitySingular: 'Account Executive',
            })
          : []),
      ];
    },
  };
};
